import { HStack, Select, VStack } from '@chakra-ui/react'
import { Field, type FormikErrors, useFormikContext } from 'formik'
import React from 'react'
import * as Yup from 'yup'

export const SESSIONS_COMPLETED_FILTER_OPERATORS = [
  { display: 'less than', value: 'less-than' },
  { display: 'less than or equal to', value: 'less-than-equal' },
  { display: 'greater than', value: 'greater-than' },
  {
    display: 'greater than or equal to',
    value: 'greater-than-equal'
  },
  { display: 'equal to', value: 'equal' },
  { display: 'between (inclusive)', value: 'between-inclusive' }
]

export const SESSIONS_COMPLETED_OPERATOR_SCHEMA = Yup.string().when(
  'filterTypes',
  {
    is: (types: string[]) => types.includes('sessionsCompleted'),
    then: Yup.string().when('sessionsCompletedUIValue', {
      is: 'firstSession',
      then: Yup.string().required(),
      otherwise: Yup.string().optional()
    }),
    otherwise: Yup.string().optional()
  }
)

export const SESSIONS_COMPLETED_VALUES_SCHEMA = Yup.array().when(
  'filterTypes',
  {
    is: (types: string[]) => types.includes('sessionsCompleted'),
    then: Yup.array().when('sessionsCompletedUIValue', {
      is: 'firstSession',
      then: Yup.array().of(Yup.number().required()).min(1).required(),
      otherwise: Yup.array().optional()
    }),
    otherwise: Yup.array().optional()
  }
)

type Props = {
  errors: FormikErrors<{
    sessionsCompletedValues: string[]
    sessionsCompletedOperator: string | undefined
    sessionsCompletedUIValue: string | undefined
  }>
  resetForm: (values: object) => void
  constants: {
    operators: {
      display: string
      value: string
    }[]
  }
}

type FormikContext = {
  values: {
    sessionsCompletedValues: string[]
    sessionsCompletedOperator: string | undefined
    sessionsCompletedUIValue: string | undefined
  }
  errors: FormikErrors<{
    sessionsCompletedValues: string[]
    sessionsCompletedOperator: string | undefined
    sessionsCompletedUIValue: string | undefined
  }>
}

export const SessionsCompletedFilter = ({ resetForm, constants }: Props) => {
  const { values, errors }: FormikContext = useFormikContext()

  const options = [
    { display: 'First Session', value: 'firstSession' },
    { display: 'Every Session', value: 'everySession' }
  ]

  const handleUIValueChange = (selectedValue: string) => {
    if (selectedValue === 'firstSession') {
      resetForm({
        ...values,
        sessionsCompletedUIValue: selectedValue,
        sessionsCompletedOperator: 'equal',
        sessionsCompletedValues: [1]
      })
    } else if (selectedValue === 'everySession') {
      resetForm({
        ...values,
        sessionsCompletedUIValue: selectedValue,
        sessionsCompletedOperator: '',
        sessionsCompletedValues: []
      })
    }
  }

  return (
    <HStack spacing="small" align="flex-start" justify="flex-start" w="100%">
      <VStack w="100%" spacing="small" display="flex" alignItems="flex-start">
        <Field name="sessionsCompletedUIValue">
          {({ field }: { field: { value: string } }) => (
            <Select
              {...field}
              value={field.value || ''}
              borderColor={
                errors?.sessionsCompletedUIValue ? 'error' : 'light_gray'
              }
              color={field.value ? 'black' : 'gray'}
              mt="xsmall"
              onChange={e => handleUIValueChange(e.target.value)}
            >
              <option value="" disabled>
                Which sessions?
              </option>
              {options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.display}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </VStack>
    </HStack>
  )
}
